import dayjs from 'dayjs';

import { type DataKey } from '@cast/design-system';
import {
  ConvertableToDateTime,
  PickByType,
  TimeUnit,
} from '@cast/shared-types';
import { toNumOrZero } from '@cast/utils';

import { DoublyLinkedDatapoint } from 'types/charts';
import { TimeSeries, TimeSeriesWithTicks } from 'types/metrics';

import { anyTimestampToUnix } from './date';

type TickMapper<T extends object = object> = (timeSeries: T[]) => number[];

/**
 * Returns a function that, based on the interval setting,
 * returns a tick mapper for the given timeseries data.
 */
export const getTickMapper =
  <T extends object>(
    interval: TimeUnit,
    timestampKey: keyof PickByType<T, number>
  ): TickMapper<T> =>
  (timeSeries) => {
    if (!timeSeries.length) {
      return [];
    }

    // Store the first timestamp for each interval
    const intervalMap = new Map<number, number>();

    // Group timestamps by their interval start time
    for (const item of timeSeries) {
      const timestamp = item[timestampKey] as number;
      const intervalStart = dayjs.unix(timestamp).startOf(interval).unix();

      // Keep the first timestamp we see for each interval
      if (!intervalMap.has(intervalStart)) {
        intervalMap.set(intervalStart, timestamp);
      }
    }

    // Convert to array and sort by original timestamps
    return Array.from(intervalMap.values()).sort((a, b) => a - b);
  };

/**
 * Generates key which is used as x-axis dataKey
 * */
export const attachTimeTicks = <T extends object>(
  entries: TimeSeries<T>,
  key: keyof T = 'timestamp' as keyof T
): TimeSeriesWithTicks<T> => {
  return entries.map((entry) => ({
    ...entry,
    chartTimestampUnix: anyTimestampToUnix(entry[key] as ConvertableToDateTime),
  }));
};

export const attachDoubleLinks = <T>(data: T[]): DoublyLinkedDatapoint<T>[] => {
  return data.map((item, index, arr) => ({
    ...item,
    prev: arr[index - 1],
    next: arr[index + 1],
  }));
};

export const getUnixTimeFormat = <T extends object>(
  data: TimeSeries<T>,
  timestampKey = 'chartTimestampUnix'
): string => {
  if (!data.length) return 'hh:mm:ss A';

  const timestamps = data.map((d: any) => d[timestampKey]);
  const min = Math.min(...timestamps);
  const max = Math.max(...timestamps);
  const range = dayjs.duration(Math.abs(max - min) * 1_000);

  const rangeInMinutes = range.asMinutes();

  if (rangeInMinutes <= 5) {
    // Less than 5 minutes
    return 'hh:mm:ss A';
  } else if (rangeInMinutes <= 480) {
    // Less than 8 hours
    return 'hh:mm A';
  } else if (rangeInMinutes <= 1_680) {
    // Less than 28 hours
    return 'hh A';
  } else if (rangeInMinutes <= 10_320) {
    // Less than 7 days and 4 hours
    return 'MMM DD';
  } else if (rangeInMinutes < 525_840) {
    // Less than 366 days and 4 hours
    return 'DD';
  } else {
    // 1 year or more
    return 'YYYY-MM-DD';
  }
};

// Create a single canvas instance for all measurements
let canvas: HTMLCanvasElement | null = null;
let context: CanvasRenderingContext2D | null = null;

const getCanvasContext = (): CanvasRenderingContext2D | null => {
  if (!canvas) {
    canvas = document.createElement('canvas');
    context = canvas.getContext('2d');
  }
  return context;
};

// Cache for measured text widths
const textWidthCache = new Map<string, number>();

const measureTextWidth = (
  text: string,
  fontSize: number,
  fontFamily: string = 'Arial'
): number => {
  const cacheKey = `${text}-${fontSize}-${fontFamily}`;
  const cachedWidth = textWidthCache.get(cacheKey);
  if (cachedWidth !== undefined) {
    return cachedWidth;
  }

  const ctx = getCanvasContext();
  if (!ctx) return fontSize * 4; // Fallback to estimation if canvas is not available

  ctx.font = `${fontSize}px ${fontFamily}`;
  const width = ctx.measureText(text).width;
  textWidthCache.set(cacheKey, width);
  return width;
};

type CalculateChartTicksParams = {
  timestamps: number[];
  timezone?: string;
  format: string;
  fontSize: number;
  chartWidth: number;
  fontFamily?: string;
};

/**
 * Calculates the optimal time interval between ticks based on format and available space
 */
export const calculateTimeInterval = (
  format: string,
  totalSpan: number,
  maxTicks: number
): number => {
  if (format.includes('ss')) {
    return 60;
  }

  if (format.includes('mm') || format.includes('hh:mm')) {
    const minutesPerTick = Math.ceil(totalSpan / 60 / maxTicks);
    if (minutesPerTick <= 5) return 300;
    if (minutesPerTick <= 15) return 900;
    if (minutesPerTick <= 30) return 1_800;
    return 3_600;
  }

  if (format.includes('HH') || format.includes('hh')) {
    const rangeInHours = totalSpan / 3_600;
    const ticksPerHour = Math.ceil(maxTicks / rangeInHours);
    if (ticksPerHour >= 2) return 1_800;
    if (ticksPerHour >= 1.5) return 2_700;
    return 3_600;
  }

  if (format.includes('DD') || format.includes('MMM')) {
    const rangeInDays = totalSpan / 86_400;
    const ticksPerDay = Math.ceil(maxTicks / rangeInDays);
    if (ticksPerDay >= 2) return 43_200;
    return 86_400;
  }

  return Math.ceil(totalSpan / maxTicks);
};

type IsDifferentTimePeriodParams = {
  t1: number;
  t2: number;
  timezone?: string;
  format: string;
};

/**
 * Checks if two timestamps are in different time periods based on the format
 */
export const isDifferentTimePeriod = ({
  t1,
  t2,
  timezone,
  format,
}: IsDifferentTimePeriodParams): boolean => {
  const date1 = dayjs.unix(t1).tz(timezone);
  const date2 = dayjs.unix(t2).tz(timezone);

  // Define the time units to check based on format
  const timeUnits = [
    { pattern: 'YYYY', unit: 'year' as const },
    { pattern: 'MMM', unit: 'month' as const },
    { pattern: 'DD', unit: 'date' as const },
    { pattern: 'HH', unit: 'hour' as const },
    { pattern: 'hh', unit: 'hour' as const },
    { pattern: 'mm', unit: 'minute' as const },
    { pattern: 'ss', unit: 'second' as const },
  ];

  // Find the most significant time unit in the format
  const significantUnit = timeUnits.find(({ pattern }) =>
    format.includes(pattern)
  );

  if (!significantUnit) return false;

  // Check if the timestamps differ in the significant unit or any more significant units
  const unitIndex = timeUnits.findIndex(
    ({ unit }) => unit === significantUnit.unit
  );
  return timeUnits
    .slice(0, unitIndex + 1)
    .some(({ unit }) => date1.get(unit) !== date2.get(unit));
};

type CalculateMaxTickWidthParams = {
  timestamps: number[];
  timezone?: string;
  format: string;
  fontSize: number;
  fontFamily: string;
};

/**
 * Calculates the maximum width needed for tick labels
 */
const calculateMaxTickWidth = ({
  timestamps,
  timezone,
  format,
  fontSize,
  fontFamily,
}: CalculateMaxTickWidthParams): number => {
  let maxTickWidth = 0;
  const formattedSet = new Set<string>();

  timestamps.forEach((ts) => {
    const formatted = dayjs.unix(ts).tz(timezone).format(format);
    if (!formattedSet.has(formatted)) {
      formattedSet.add(formatted);
      const width = measureTextWidth(formatted, fontSize, fontFamily);
      maxTickWidth = Math.max(maxTickWidth, width);
    }
  });

  return maxTickWidth;
};

/**
 * Checks if two ticks would overlap visually
 */
export const willOverlap = (
  t1: number,
  t2: number,
  context: {
    startTime: number;
    totalSpan: number;
    chartWidth: number;
    minGap: number;
  }
): boolean => {
  const { chartWidth, minGap } = context;
  let { startTime, totalSpan } = context;

  // Handle special cases
  if (!isFinite(t1) || !isFinite(t2) || !isFinite(startTime)) return false;
  if (!isFinite(totalSpan) || !isFinite(chartWidth) || !isFinite(minGap))
    return false;
  if (totalSpan === 0 || chartWidth === 0) return true;
  if (minGap <= 0) return false;

  // Handle negative totalSpan
  if (totalSpan < 0) {
    // For negative totalSpan, we treat it as if the range is reversed
    totalSpan = Math.abs(totalSpan);
    startTime = startTime - totalSpan;
  }

  // Calculate pixel positions
  const px1 = ((t1 - startTime) / totalSpan) * chartWidth;
  const px2 = ((t2 - startTime) / totalSpan) * chartWidth;
  return Math.abs(px1 - px2) < minGap;
};

/**
 * Checks if a new tick would overlap with any existing ticks
 */
export const willOverlapWithAny = (
  tick: number,
  existingTicks: number[],
  context: {
    startTime: number;
    totalSpan: number;
    chartWidth: number;
    minGap: number;
  }
): boolean => {
  return existingTicks.some((existingTick) =>
    willOverlap(tick, existingTick, context)
  );
};

/**
 * Calculates optimal tick positions for a time series chart,
 * ensuring ticks are visually evenly spaced and aligned with actual data points.
 */
export const calculateTimeSeriesTicks = ({
  timestamps,
  timezone,
  format,
  fontSize,
  chartWidth,
  fontFamily = 'Arial',
}: CalculateChartTicksParams): number[] => {
  if (!timestamps.length) return [];

  // Calculate maximum number of ticks that can fit
  const maxTickWidth = calculateMaxTickWidth({
    timestamps,
    timezone,
    format,
    fontSize,
    fontFamily,
  });
  const minGapBetweenTicks = Math.max(maxTickWidth * 1.2, maxTickWidth + 12);
  const maxTicks = Math.floor(chartWidth / minGapBetweenTicks);

  if (maxTicks < 2) {
    return [timestamps[0], timestamps[timestamps.length - 1]];
  }

  // Calculate time interval between ticks
  const totalSpan = timestamps[timestamps.length - 1] - timestamps[0];
  const timeInterval = calculateTimeInterval(format, totalSpan, maxTicks);

  // Generate ticks only for actual data points
  const ticks: number[] = [];
  const seen = new Map<string, number>();
  const formatTick = (tick: number): string =>
    dayjs.unix(tick).tz(timezone).format(format);

  // Find the first data point that matches our interval
  let currentIndex = 0;
  while (currentIndex < timestamps.length) {
    const currentTime = timestamps[currentIndex];
    const formatted = formatTick(currentTime);
    const lastSeenTime = seen.get(formatted);

    // Check if we should add this tick based on time period and overlap rules
    const isNewTimePeriod =
      !lastSeenTime ||
      isDifferentTimePeriod({
        t1: currentTime,
        t2: lastSeenTime,
        format,
        timezone,
      });
    const wouldOverlap = willOverlapWithAny(currentTime, ticks, {
      startTime: timestamps[0],
      totalSpan,
      chartWidth,
      minGap: minGapBetweenTicks,
    });

    if (isNewTimePeriod && !wouldOverlap) {
      seen.set(formatted, currentTime);
      ticks.push(currentTime);
    }

    // Move to the next data point that's at least timeInterval away
    const nextTime = currentTime + timeInterval;
    while (
      currentIndex < timestamps.length &&
      timestamps[currentIndex] < nextTime
    ) {
      currentIndex++;
    }
  }

  return ticks;
};

/**
 * Calculates ticks for discrete data charts (bar) using a step-based approach.
 * Shows ticks at regular intervals (every Nth data point) based on available space.
 * Optimized for bar charts where each tick represents a discrete data point.
 */
export const calculateStepTicks = ({
  timestamps,
  timezone,
  format,
  fontSize,
  chartWidth,
  fontFamily = 'Arial',
}: CalculateChartTicksParams): number[] => {
  if (!timestamps.length) return [];

  // Calculate maximum number of ticks that can fit
  const maxTickWidth = calculateMaxTickWidth({
    timestamps,
    timezone,
    format,
    fontSize,
    fontFamily,
  });
  const minGapBetweenTicks = Math.max(maxTickWidth * 1.2, maxTickWidth + 12);
  const maxTicks = Math.floor(chartWidth / minGapBetweenTicks);
  const formatTick = (tick: number): string =>
    dayjs.unix(tick).tz(timezone).format(format);

  // If we can fit all ticks, return only visually unique ticks
  if (maxTicks >= timestamps.length) {
    const ticks: number[] = [];
    const seen = new Map<string, number>();
    for (const t of timestamps) {
      const formatted = formatTick(t);
      const lastSeenTime = seen.get(formatted);

      if (
        !lastSeenTime ||
        isDifferentTimePeriod({ t1: t, t2: lastSeenTime, format, timezone })
      ) {
        seen.set(formatted, t);
        ticks.push(t);
      }
    }
    return ticks;
  }

  // If we can't fit at least 2 ticks, return just min and max (if visually unique)
  if (maxTicks < 2) {
    const ticks: number[] = [];
    const seen = new Map<string, number>();
    for (const t of [timestamps[0], timestamps[timestamps.length - 1]]) {
      const formatted = formatTick(t);
      const lastSeenTime = seen.get(formatted);

      if (
        !lastSeenTime ||
        isDifferentTimePeriod({ t1: t, t2: lastSeenTime, format, timezone })
      ) {
        seen.set(formatted, t);
        ticks.push(t);
      }
    }
    return ticks;
  }

  // Calculate step size to evenly distribute ticks
  const step = Math.ceil(timestamps.length / maxTicks);

  // Generate ticks with the calculated step, only visually unique
  const ticks: number[] = [];
  const seen = new Map<string, number>();

  for (let i = 0; i < timestamps.length; i += step) {
    const t = timestamps[i];
    const formatted = formatTick(t);
    const lastSeenTime = seen.get(formatted);

    if (
      !lastSeenTime ||
      isDifferentTimePeriod({ t1: t, t2: lastSeenTime, format, timezone })
    ) {
      seen.set(formatted, t);
      ticks.push(t);
    }
  }

  return ticks;
};

type OverlappingDataLevel<K extends string = string> = {
  /** Unique identifier and data key for this level. */
  key: K;
  /** Data key for this level, if different from key. */
  dataKey?: string;
  /** Whether this level is currently shown in the chart */
  enabled: boolean;
};

/**
 * Calculates the dataKey function for a specific level in an overlapping chart.
 *
 * @param levels - The overlapping levels of data
 * @param targetLevelIndex - The index of the level to calculate the dataKey for
 * @returns A dataKey that can be used directly with chart components (string or function)
 */
const getOverlappingDataKey = <K extends string>(
  levels: OverlappingDataLevel<K>[],
  targetLevelIndex: number
): DataKey<unknown> => {
  const targetLevel = levels[targetLevelIndex];

  // Get all levels that come before the target level (smaller values)
  const previousLevels = levels.slice(0, targetLevelIndex);
  if (!previousLevels.length) {
    return targetLevel.dataKey ?? targetLevel.key;
  }

  // Return a function that subtracts all enabled previous levels
  return (data) => {
    const getValue = (level: OverlappingDataLevel<K>): number => {
      return toNumOrZero((data as any)?.[level.dataKey ?? level.key]);
    };

    // Find the largest enabled previous level to subtract from
    // (since levels are hierarchical, we only need to subtract the immediate previous enabled level)
    const largestPreviousLevel = previousLevels.at(-1)!;

    return getValue(targetLevel) - getValue(largestPreviousLevel);
  };
};

/**
 * Utility for creating overlapping bar charts where data points form a hierarchy
 * (each level is a superset of the previous ones in increasing order).
 *
 * This allows visual "overlapping" of bars where each bar represents the difference
 * between its level and all previously rendered levels, creating a stacked visual
 * effect while maintaining the correct proportions.
 *
 * @param levels - The overlapping levels of data in increasing order of values
 * @returns Array of objects containing original key and calculated dataKey for chart components
 */
export const generateOverlappingDataKeys = <K extends string>(
  levels: OverlappingDataLevel<K>[]
): Array<{
  key: K;
  dataKey: DataKey<unknown>;
}> => {
  const enabledLevels = levels.filter((level) => level.enabled);
  return enabledLevels.map((level, index) => ({
    key: level.key,
    dataKey: getOverlappingDataKey(enabledLevels, index),
  }));
};
